import React from 'react';
import { Input, Textarea } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useControlDisabled } from './ControlDisabledContext';
import { joinClasses } from './fieldClassSlots';

export interface OASISTextRenderProps {
  value: string;
  onChange: (v: string) => void;
  label: string;
}

export interface OASISTextProps {
  qid: string;
  className?: string;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  /** Render a textarea instead of a single-line input */
  multiline?: boolean;
  inputClassName?: string;
  children?: (props: OASISTextRenderProps) => React.ReactNode;
}

export function OASISText({
  qid,
  className,
  hideLabel,
  innerClassName,
  labelClassName,
  multiline,
  inputClassName,
  children,
}: OASISTextProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const inheritedDisabled = useControlDisabled();
  const question = schemaMap.get(qid);

  const value = (answers[qid] as string) ?? '';
  const onChange = (v: string) => onAnswerChange(qid, v);
  const label = question?.question ?? question?.description ?? qid;

  return (
    <div data-qid={qid} className={className}>
      {children ? (
        children({ value, onChange, label })
      ) : (
        <div className={innerClassName ?? 'space-y-2'}>
          {!hideLabel && (
            <label
              className={joinClasses(
                'block text-sm font-semibold text-foreground',
                labelClassName
              )}
            >
              {label}
            </label>
          )}
          {multiline ? (
            <Textarea
              disabled={inheritedDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={inputClassName}
            />
          ) : (
            <Input
              disabled={inheritedDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={inputClassName}
            />
          )}
        </div>
      )}
    </div>
  );
}
